import { Ionicons } from '@expo/vector-icons';
import React, { useCallback, useRef, useState } from 'react';
import { Modal, Platform, Pressable, ScrollView, View, useWindowDimensions } from 'react-native';
import { FadeIn, FadeOut } from 'react-native-reanimated';

import { MemberAvatar, getFirstName } from '@/components/team/member-avatar';
import { ThemedText } from '@/components/themed-text';
import { AnimatedView } from '@/components/ui/animated';
import { PressableScale } from '@/components/ui/pressable-scale';
import { Tooltip } from '@/components/ui/tooltip';
import { useMembership } from '@/hooks/use-membership';
import { useTeamMembers } from '@/hooks/use-team';
import { switchActingProvider, useActingProviderStore } from '@/store/actingProviderStore';
import { useAuthStore } from '@/store/authStore';
import { TeamMember } from '@/types/organization';
import { cn } from '@/utils/cn';
import { useThemedHex } from '@/hooks/use-theme-colors';

type Anchor = { x: number; y: number; width: number; height: number };

const POPOVER_WIDTH = 248;

/** Mede o gatilho na janela — `fixed` na web, porque a sidebar é um ScrollView. */
function measureAnchor(node: View | null, done: (anchor: Anchor | null) => void) {
  const el = node as unknown as {
    getBoundingClientRect?: () => DOMRect;
    measureInWindow?: (cb: (x: number, y: number, w: number, h: number) => void) => void;
  } | null;
  if (!el) return done(null);
  if (Platform.OS === 'web' && typeof el.getBoundingClientRect === 'function') {
    const rect = el.getBoundingClientRect();
    return done({ x: rect.left, y: rect.top, width: rect.width, height: rect.height });
  }
  if (typeof el.measureInWindow === 'function') {
    el.measureInWindow((x, y, width, height) => done({ x, y, width, height }));
    return;
  }
  done(null);
}

/**
 * Seletor de profissional: o dono troca de quem é a agenda (e todo o resto)
 * que o app mostra. Só aparece para o dono de uma equipe com mais alguém.
 *
 * - `expanded`: pill com avatar + nome + chevron, abaixo do logo;
 * - `collapsed`: só o avatar, com tooltip (sidebar recolhida);
 * - `drawer`: pill do menu mobile.
 */
export function ProfessionalSwitcher({
  variant,
  onPicked,
}: {
  variant: 'expanded' | 'collapsed' | 'drawer';
  /** Depois de trocar (o drawer mobile fecha). */
  onPicked?: () => void;
}) {
  const th = useThemedHex();
  const { can, hasTeam, membership } = useMembership();
  const visible = can('actAs') && hasTeam;
  const { data: members } = useTeamMembers({ enabled: visible });
  const user = useAuthStore((s) => s.user);
  const acting = useActingProviderStore((s) => s.acting);
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();

  const triggerRef = useRef<View>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [open, setOpen] = useState(false);

  const openPopover = useCallback(() => {
    measureAnchor(triggerRef.current, (next) => {
      setAnchor(next);
      setOpen(true);
    });
  }, []);

  const close = useCallback(() => setOpen(false), []);

  if (!visible || !user) return null;

  const others = (members ?? []).filter((m) => m.role !== 'OWNER' && m.status === 'ACTIVE');

  // Sem profissional em foco quem está na tela é o dono: o rótulo é o papel
  // ("Administrador"), e o nome do negócio só aparece como legenda/inicial.
  const businessName = membership?.organizationName?.trim() || user.username?.trim() || '';
  const ownerName = 'Administrador';
  const currentName = acting?.name ?? ownerName;
  const currentImage = acting ? acting.image : (user.image ?? null);
  const currentColor = acting?.color ?? null;
  /** O avatar da administração é a inicial do negócio; o profissional mantém as suas. */
  const currentInitials = acting ? undefined : (businessName.charAt(0) || 'A').toUpperCase();

  const pick = (member: TeamMember | null) => {
    close();
    switchActingProvider(
      member
        ? {
            providerId: member.providerId,
            name: member.name,
            image: member.image,
            color: member.color,
          }
        : null,
    );
    onPicked?.();
  };

  // Sidebar recolhida abre à direita do avatar; o resto abre embaixo.
  const beside = variant === 'collapsed';
  const popoverStyle = anchor
    ? beside
      ? {
          left: anchor.x + anchor.width + 8,
          top: Math.min(anchor.y, windowHeight - 320),
        }
      : {
          left: Math.min(anchor.x, windowWidth - POPOVER_WIDTH - 12),
          top: anchor.y + anchor.height + 6,
        }
    : { left: 16, top: 80 };

  const trigger =
    variant === 'collapsed' ? (
      <Tooltip side="right" content={currentName}>
        <PressableScale
          haptic
          onPress={openPopover}
          accessibilityRole="button"
          accessibilityLabel={`Em foco: ${currentName}. Trocar`}
          className="items-center justify-center rounded-full p-1 active:bg-neutral-100"
        >
          <MemberAvatar
            name={currentName}
            image={currentImage}
            color={currentColor}
            initials={currentInitials}
            size={32}
          />
        </PressableScale>
      </Tooltip>
    ) : (
      <PressableScale
        haptic
        onPress={openPopover}
        accessibilityRole="button"
        accessibilityLabel={`Em foco: ${currentName}. Trocar`}
        className={cn(
          'flex-row items-center gap-2 rounded-full border border-neutral-200 bg-card pl-1.5 pr-3',
          variant === 'drawer' ? 'h-12' : 'h-10',
        )}
        style={{ borderCurve: 'continuous' }}
      >
        <MemberAvatar
          name={currentName}
          image={currentImage}
          color={currentColor}
          initials={currentInitials}
          size={variant === 'drawer' ? 34 : 28}
        />
        <ThemedText
          numberOfLines={1}
          className={cn(
            'flex-1 font-semibold text-neutral-900',
            variant === 'drawer' ? 'text-sm' : 'text-[13px]',
          )}
        >
          {acting ? getFirstName(currentName) : ownerName}
        </ThemedText>
        <Ionicons className="text-neutral-500" name="chevron-down" size={14} />
      </PressableScale>
    );

  return (
    <>
      <View
        ref={triggerRef}
        collapsable={false}
        className={cn(
          variant === 'collapsed'
            ? 'mb-1 items-center'
            : variant === 'drawer'
              ? 'mx-4 mt-3'
              : 'mx-2 mb-2',
        )}
      >
        {trigger}
      </View>

      <Modal transparent visible={open} animationType="none" onRequestClose={close}>
        <View className="flex-1">
          <Pressable
            className="flex-1"
            style={{ backgroundColor: 'rgba(23, 17, 10, 0.28)' }}
            onPress={close}
            accessibilityLabel="Fechar"
          />
          <AnimatedView
            entering={FadeIn.duration(180)}
            exiting={FadeOut.duration(120)}
            className="absolute overflow-hidden rounded-2xl border border-neutral-900/5 bg-card p-1.5"
            style={[
              popoverStyle,
              {
                width: POPOVER_WIDTH,
                maxHeight: 360,
                backgroundColor: th('#FFFFFF'),
                borderCurve: 'continuous',
                boxShadow: '0 12px 32px -8px rgba(23, 17, 10, 0.18)',
              },
              Platform.OS === 'web' ? ({ position: 'fixed' } as never) : null,
            ]}
          >
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <SwitcherRow
                name={ownerName}
                image={user.image ?? null}
                color={null}
                initials={(businessName.charAt(0) || 'A').toUpperCase()}
                caption={businessName || undefined}
                selected={!acting}
                onPress={() => pick(null)}
              />
              {others.map((member) => (
                <SwitcherRow
                  key={member.id}
                  name={member.name}
                  image={member.image}
                  color={member.color}
                  caption={member.joinedAt ? undefined : 'Aguardando entrar'}
                  selected={acting?.providerId === member.providerId}
                  onPress={() => pick(member)}
                />
              ))}
            </ScrollView>
          </AnimatedView>
        </View>
      </Modal>
    </>
  );
}

function SwitcherRow({
  name,
  image,
  color,
  caption,
  initials,
  selected,
  onPress,
}: {
  name: string;
  image: string | null;
  color: string | null;
  caption?: string;
  initials?: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <PressableScale
      haptic
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      className={cn(
        'h-12 flex-row items-center gap-2.5 rounded-xl px-2.5',
        selected ? 'bg-primary/5' : 'active:bg-neutral-50',
      )}
    >
      <MemberAvatar name={name} image={image} color={color} initials={initials} size={30} />
      <View className="min-w-0 flex-1">
        <ThemedText
          numberOfLines={1}
          className={cn(
            'text-sm',
            selected ? 'font-semibold text-neutral-900' : 'text-neutral-700',
          )}
        >
          {name}
        </ThemedText>
        {caption ? (
          <ThemedText numberOfLines={1} className="text-xs text-neutral-500">
            {caption}
          </ThemedText>
        ) : null}
      </View>
      {selected ? <Ionicons name="checkmark-circle" size={18} color="#FD5001" /> : null}
    </PressableScale>
  );
}
